import React, { useState, useEffect, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import Select from 'react-select';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function OtherKPIReport() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [branchOptions, setBranchOptions] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [totalNetSales, setTotalNetSales] = useState(0);
    const [totalNumberofPcs, setTotalNumberofPcs] = useState(0);
    const [totalNumberofBills, setTotalNumberofBills] = useState(0);
    const [totalSingleBills, setTotalSingleBills] = useState(0);
    const [totalASP, setTotalASP] = useState(0);
    const [totalUPT, setTotalUPT] = useState(0);
    const [totalVPT, setTotalVPT] = useState(0);
    const [totalPercentageSingleBill, setTotalPercentageSingleBill] = useState(0);
    const [totalNumberofWatches, setTotalNumberofWatches] = useState(0);
    const [totalNumberofJewellery, setTotalNumberofJewellery] = useState(0);
    const [totalASPJewellery, setTotalASPJewellery] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        const startDate = getFormattedStartDate();
        const currentDate = getFormattedCurrentDate();
        setFromDate(startDate);
        setToDate(currentDate);
        fetchBranchData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData(fromDate, toDate);
        }
    }, [fromDate, toDate]);

    const handleBranchSelect = (opts) => { setSelectedBranches(opts); setDataItem([]); };

    const fetchBranchData = () => {
        fetchData('ReportApi', `/Branch/${companyId}/${branchId}`, token)
            .then(r => setBranchOptions(r.response_data.map(c => ({ label: c.Name, value: c.Id }))))
            .catch(console.error);
    };

    useEffect(() => {
        let ns = 0, pcs = 0, bills = 0, single = 0, watches = 0, jewellery = 0, aspJew = 0, netJew = 0;
        dataItem.forEach(item => { ns += item.NetSales; pcs += item.NumberofPcs; bills += item.NumberofBills; single += item.NumberofSingleBills; watches += item.NumberofWatches; jewellery += item.NumberofJewellery; aspJew += item.ASPJewellery; netJew += item.NetSalesJewellery; });
        setTotalNetSales(ns); setTotalNumberofPcs(pcs); setTotalNumberofBills(bills); setTotalSingleBills(single); setTotalNumberofWatches(watches); setTotalNumberofJewellery(jewellery);
        setTotalASP(pcs > 0 ? Math.round(ns / pcs) : 0);
        setTotalUPT(bills > 0 ? pcs / bills : 0);
        setTotalVPT(bills > 0 ? ns / bills : 0);
        setTotalPercentageSingleBill(bills > 0 ? (single * 100) / bills : 0);
        setTotalASPJewellery(jewellery > 0 ? Math.round(netJew / jewellery) : 0);
    }, [dataItem]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);
    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const fetchTableData = async (fDate, tDate) => {
        const effectiveFromDate = fDate || fromDate;
        const effectiveToDate = tDate || toDate;
        if (hasReadReportPermission && !loadingRef.current && effectiveFromDate && effectiveToDate) {
            setDataItem([]); setLoading(true);
            loadingRef.current = true;
            const branchIds = selectedBranches.length === 0 ? branchOptions.map(b => b.value) : selectedBranches.map(b => b.value);
            try {
                const response = await fetchData('OtherReportApi', `/KPIReport/${companyId}/${branchIds.join(',')}/${effectiveFromDate}/${effectiveToDate}`, token);
                if (response.responseCode === '1111') setDataItem(response.response_data);
            } catch (e) { console.error(e); } finally { loadingRef.current = false; setLoading(false); }
        }
    };

    const handleSearch = (e) => setSearchQuery(e.target.value);
    const filteredDataItem = dataItem.filter(item => Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase())));
    const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });

    // const handleExportExcel = async () => {
    //     const workbook = new ExcelJS.Workbook();
    //     const worksheet = workbook.addWorksheet('KPI Report');
    //     const rightAlignColumns = ['Net Sales','Number of Pcs','Number of Bills','Number of Single Bills','ASP','UPT','VPT','% Single Bills','Number of Watches','ASP Jewellery'];
    //     worksheet.mergeCells('A1:L1');
    //     const hc = worksheet.getCell('A1');
    //     hc.value = `KPI Report From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
    //     hc.font = { bold: true, size: 14 }; hc.alignment = { vertical: 'middle', horizontal: 'left' };
    //     const headerRowValues = ['Store','Staff Name','Net Sales','Number of Pcs','Number of Bills','Number of Single Bills','ASP','UPT','VPT','% Single Bills','Number of Watches','ASP Jewellery'];
    //     const headerRow = worksheet.addRow(headerRowValues);
    //     headerRow.eachCell(cell => { cell.fill = { type:'pattern',pattern:'solid',fgColor:{argb:'FFB6D7A8'} }; cell.font = { bold:true,color:{argb:'FF000000'} }; cell.alignment = { vertical:'middle',horizontal:'center',wrapText:true }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
    //     worksheet.columns = headerRowValues.map(() => ({ width: 18 }));
    //     filteredDataItem.forEach(rowData => {
    //         const row = worksheet.addRow([rowData.StoreName,rowData.StaffName,rowData.NetSales,rowData.NumberofPcs,rowData.NumberofBills,rowData.NumberofSingleBills,rowData.ASP,parseFloat(rowData.UPT.toFixed(3)),parseFloat(rowData.VPT.toFixed(0)),parseFloat(rowData.PercentageSingleBill.toFixed(1)),rowData.NumberofWatches,rowData.ASPJewellery]);
    //         row.eachCell((cell, col) => { cell.alignment = { vertical:'middle',horizontal:rightAlignColumns.includes(headerRowValues[col-1])?'right':'center' }; if (headerRowValues[col-1]==='UPT') cell.numFmt='0.000'; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
    //     });
    //     const totalRow = worksheet.addRow(['','Total',totalNetSales,totalNumberofPcs,totalNumberofBills,totalSingleBills,totalASP,parseFloat(totalUPT.toFixed(3)),parseFloat(totalVPT.toFixed(0)),parseFloat(totalPercentageSingleBill.toFixed(1)),totalNumberofWatches,totalASPJewellery]);
    //     totalRow.font = { bold: true };
    //     totalRow.eachCell((cell, col) => { cell.alignment = { vertical:'middle',horizontal:rightAlignColumns.includes(headerRowValues[col-1])?'right':'center' }; if (headerRowValues[col-1]==='UPT') cell.numFmt='0.000'; cell.fill = { type:'pattern',pattern:'solid',fgColor:{argb:'FFFFF2CC'} }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
    //     const buffer = await workbook.xlsx.writeBuffer();
    //     saveAs(new Blob([buffer]), 'KPIReport.xlsx');
    // };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('KPI Report');

        // Define columns that should be right-aligned
        const rightAlignColumns = [
            'Net Sales', 'Number of Pcs', 'Number of Bills', 'Number of Single Bills',
            'ASP', 'UPT', 'VPT', '% Single Bills', 'Number of Watches', 'ASP Jewellery'
        ];

        // Caption row
        const headerCaption = `KPI Report From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        worksheet.mergeCells('A1:L1');
        const headerCaptionCell = worksheet.getCell('A1');
        headerCaptionCell.value = headerCaption;
        headerCaptionCell.font = { bold: true, size: 14 };
        headerCaptionCell.alignment = { vertical: 'middle', horizontal: 'left' };

        // Main header row
        const headerRowValues = [
            'Store', 'Staff Name', 'Net Sales', 'Number of Pcs', 'Number of Bills', 'Number of Single Bills',
            'ASP', 'UPT', 'VPT', '% Single Bills', 'Number of Watches', 'ASP Jewellery'
        ];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));

        // Data rows
        filteredDataItem.forEach((rowData) => {
            const dataRow = [
                rowData.StoreName,
                rowData.StaffName,
                rowData.NetSales,
                rowData.NumberofPcs,
                rowData.NumberofBills,
                rowData.NumberofSingleBills,
                rowData.ASP,
                parseFloat(rowData.UPT.toFixed(3)),
                parseFloat(rowData.VPT.toFixed(0)),
                parseFloat(rowData.PercentageSingleBill.toFixed(1)),
                rowData.NumberofWatches,
                rowData.ASPJewellery
            ];

            const row = worksheet.addRow(dataRow);
            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                if (rightAlignColumns.includes(headerName)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }
                // Apply number format for UPT column
                if (headerName === 'UPT') {
                    cell.numFmt = '0.000';
                }
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        // Total row
        const totalRowValues = [
            '', 'Total',
            totalNetSales,
            totalNumberofPcs,
            totalNumberofBills,
            totalSingleBills,
            totalASP,
            parseFloat(totalUPT.toFixed(3)),
            parseFloat(totalVPT.toFixed(0)),
            parseFloat(totalPercentageSingleBill.toFixed(1)),
            totalNumberofWatches,
            totalASPJewellery
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            const headerName = headerRowValues[colNumber - 1];
            if (rightAlignColumns.includes(headerName)) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            } else {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        // Blank rows before group section
        worksheet.addRow([]);
        worksheet.addRow([]);

        // Group totals calculation
        const storeTotals = {};
        filteredDataItem.forEach(rowData => {
            if (!storeTotals[rowData.StoreName]) {
                storeTotals[rowData.StoreName] = {
                    NetSales: 0,
                    NumberofPcs: 0,
                    NumberofBills: 0,
                    NumberofSingleBills: 0,
                    NumberofWatches: 0,
                    NumberofJewellery: 0,
                    NumberofNetSalesJewellery: 0
                };
            }
            storeTotals[rowData.StoreName].NetSales += rowData.NetSales;
            storeTotals[rowData.StoreName].NumberofPcs += rowData.NumberofPcs;
            storeTotals[rowData.StoreName].NumberofBills += rowData.NumberofBills;
            storeTotals[rowData.StoreName].NumberofSingleBills += rowData.NumberofSingleBills;
            storeTotals[rowData.StoreName].NumberofWatches += rowData.NumberofWatches;
            storeTotals[rowData.StoreName].NumberofJewellery += rowData.NumberofJewellery;
            storeTotals[rowData.StoreName].NumberofNetSalesJewellery += rowData.NetSalesJewellery;
        });

        // Add calculated ASP, UPT, etc.
        Object.keys(storeTotals).forEach(storeName => {
            const store = storeTotals[storeName];
            store.ASP = store.NumberofPcs > 0 ? Math.round(store.NetSales / store.NumberofPcs) : 0;
            store.UPT = store.NumberofBills > 0 ? (store.NumberofPcs / store.NumberofBills).toFixed(3) : "0.000";
            store.VPT = store.NumberofBills > 0 ? (store.NetSales / store.NumberofBills).toFixed(0) : "0";
            store.PercentageSingleBill = store.NumberofBills > 0 ? ((store.NumberofSingleBills * 100) / store.NumberofBills).toFixed(1) : "0.0";
            store.ASPJewellery = store.NumberofJewellery > 0 ? Math.round(store.NumberofNetSalesJewellery / store.NumberofJewellery) : 0;
        });

        // Store totals header row
        const storeHeaderRow = worksheet.addRow(['', 'Store', 'Net Sales', 'Number of Pcs', 'Number of Bills', 'Number of Single Bills', 'ASP', 'UPT', 'VPT', '% Single Bills', 'Number of Watches', 'ASP Jewellery']);
        storeHeaderRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        // Add each store's row
        Object.keys(storeTotals).forEach(storeName => {
            const store = storeTotals[storeName];
            const row = worksheet.addRow([
                '',
                storeName,
                store.NetSales,
                store.NumberofPcs,
                store.NumberofBills,
                store.NumberofSingleBills,
                store.ASP,
                parseFloat(store.UPT),
                parseFloat(store.VPT),
                parseFloat(store.PercentageSingleBill),
                store.NumberofWatches,
                store.ASPJewellery
            ]);
            row.eachCell((cell, colNumber) => {
                const headerName = storeHeaderRow.getCell(colNumber).value;
                if (rightAlignColumns.includes(headerName)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }
                // Apply number format for UPT column
                if (headerName === 'UPT') {
                    cell.numFmt = '0.000';
                }
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        // Final total row for group section
        const storeTotalRow = worksheet.addRow([
            '', 'Total',
            totalNetSales,
            totalNumberofPcs,
            totalNumberofBills,
            totalSingleBills,
            totalASP,
            parseFloat(totalUPT.toFixed(3)),
            parseFloat(totalVPT.toFixed(0)),
            parseFloat(totalPercentageSingleBill.toFixed(1)),
            totalNumberofWatches,
            totalASPJewellery
        ]);
        storeTotalRow.font = { bold: true };
        storeTotalRow.eachCell((cell, colNumber) => {
            const headerName = storeHeaderRow.getCell(colNumber).value;
            if (rightAlignColumns.includes(headerName)) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            } else {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }

            // Apply number format for UPT column
            if (headerName === 'UPT') {
                cell.numFmt = '0.000';
            }

            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        // Freeze the first header row
        worksheet.views = [{ state: 'frozen', ySplit: 2 }];

        // Save file
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'KPIReport.xlsx');
    };


    const branchOptionsWithBlank = [{ label: '--Select--', value: 0 }, ...branchOptions];

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">KPI Report</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field"><label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label><input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} /></div>
                        <div className="pg-field"><label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label><input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} /></div>
                        <div className="pg-field" style={{ minWidth: 220 }}>
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>Branch <span style={{ color: 'red' }}>*</span></label>
                            <Select options={branchOptionsWithBlank} value={selectedBranches} onChange={handleBranchSelect} placeholder="Select Branch" isMulti menuPortalTarget={document.body} menuPosition="fixed" styles={{ control: b => ({ ...b, minHeight: 31, height: 31, fontSize: 12.5, borderColor: 'rgba(200,212,224,0.7)', borderRadius: 6 }), valueContainer: b => ({ ...b, height: 31, padding: '0 6px' }), indicatorsContainer: b => ({ ...b, height: 31 }), menu: b => ({ ...b, fontSize: 12.5 }), menuPortal: b => ({ ...b, zIndex: 9999 }) }} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={() => fetchTableData()} disabled={loading}><RefreshIcon /> {loading ? 'Loading...' : 'Refresh'}</button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}><FeedIcon /> Excel</button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}><SearchIcon /> Search</button>
                    </div>
                    {showSearchInput && <input className="pg-input pg-search-input" placeholder="Search…" value={searchQuery} onChange={handleSearch} autoFocus style={{ marginTop: 6 }} />}
                </div>
            </div>
            {loading && <LoadingOverlay />}
            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead><tr><th>Store</th><th>Staff Name</th><th>Net Sales</th><th>Number of Pcs</th><th>Number of Bills</th><th>Number of Single Bills</th><th>ASP</th><th>UPT</th><th>VPT</th><th>% Single Bills</th><th>Number of Watches</th><th>ASP Jewellery</th></tr></thead>
                    <tbody>
                        {filteredDataItem.map((data, i) => (
                            <tr key={i}><td>{data.StoreName}</td><td>{data.StaffName}</td><td>{data.NetSales}</td><td>{data.NumberofPcs}</td><td>{data.NumberofBills}</td><td>{data.NumberofSingleBills}</td><td>{data.ASP}</td><td>{Number(data.UPT).toFixed(3)}</td><td>{data.VPT}</td><td>{Number(data.PercentageSingleBill).toFixed(1)}</td><td>{data.NumberofWatches}</td><td>{data.ASPJewellery}</td></tr>
                        ))}
                        <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                            <td></td><td>Total</td><td>{totalNetSales}</td><td>{totalNumberofPcs}</td><td>{totalNumberofBills}</td><td>{totalSingleBills}</td><td>{totalASP}</td><td>{totalUPT.toFixed(3)}</td><td>{totalVPT.toFixed(0)}</td><td>{totalPercentageSingleBill.toFixed(1)}</td><td>{totalNumberofWatches}</td><td>{totalASPJewellery}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default OtherKPIReport;
